import React, { useMemo, useState } from 'react';
import InfoTip from '../../components/InfoTip';
import DetailModal from './DetailModal';
import { Clic, Stat, SectionTitle } from './RapportView';
import { fmtMt, fmtNb, fmtDateFr } from './format';
import { aggChantiers, noteScore, pctOuvriers, valeursKpi, fmtL, fmtH } from './formatTechnique';

// Contenu chiffré du Point du jour technique (mêmes calculs que /dashboard) : score, hier, période de paie,
// état actuel, tableau société → chantier. Chaque chiffre ouvre le détail figé (data.details).

const lvColor = (s) => (s >= 80 ? '#2e7d32' : s >= 50 ? '#e65100' : '#b71c1c');

const RapportViewTechnique = ({ data }) => {
  const [detail, setDetail] = useState(null);

  const calc = useMemo(() => {
    if (!data) return null;
    const allIds = new Set(data.chantiers.map(c => c.id));
    const societes = [];
    for (const c of data.chantiers) {
      let s = societes.find(x => x.societe === c.societe);
      if (!s) { s = { societe: c.societe, chantiers: [] }; societes.push(s); }
      s.chantiers.push({ ...c, ids: new Set([c.id]), agg: aggChantiers(data, new Set([c.id])) });
    }
    societes.forEach(s => { s.ids = new Set(s.chantiers.map(c => c.id)); s.agg = aggChantiers(data, s.ids); });
    return { allIds, total: aggChantiers(data, allIds), societes };
  }, [data]);

  if (!calc) return null;
  const { allIds } = calc;
  const T = calc.total;
  const note = noteScore(data.score.global);
  const diff = data.score.global - data.scoreVeille.global;
  const o = data.periodeChiffres.ouvriers;
  const nbKpi = valeursKpi(data);

  const voir = (kind, titre, ids = allIds) => (data.details ? () => setDetail({ kind, titre, ids }) : null);
  const voirSi = (n, kind, titre, ids) => (n ? voir(kind, titre, ids) : null);
  // KPI du score → détail correspondant
  const KPI_DETAIL = {
    'DASH-04': ['citernesBas', 'Citernes en stock bas'], 'DASH-05': ['vidDepasse', 'Vidanges dépassées'],
    'DASH-06': ['vidCritique', 'Vidanges critiques'], 'DASH-07': ['vidAlerte', 'Vidanges en alerte'],
    'DASH-11': ['reparationsPeriode', 'Réparations de la période'],
  };

  const ligne = (label, a, ids, { bold, indent } = {}) => (
    <tr key={label} style={bold ? { fontWeight: 700, background: '#f5f5db' } : undefined}>
      <td style={{ textAlign: 'left', paddingLeft: indent ? 24 : 8, whiteSpace: 'nowrap' }}>{label}</td>
      <td><Clic onClick={voirSi(a.hier.ouvNb, 'ouvriersHier', `Ouvriers pointés hier — ${label}`, ids)}>{fmtNb(a.hier.ouvNb)}</Clic>
        {a.hier.cout ? <small className="text-muted"> · {fmtMt(a.hier.cout)}</small> : null}</td>
      <td><Clic onClick={voirSi(a.hier.matNb, 'materielsHier', `Matériels pointés hier — ${label}`, ids)}>{fmtNb(a.hier.matNb)}</Clic>
        {a.hier.litres ? <small className="text-muted"> · {fmtL(a.hier.litres)}</small> : null}</td>
      <td>{fmtMt(a.periode.masse)} <small className="text-muted">· {fmtNb(a.periode.jours)} j</small></td>
      <td><Clic onClick={voirSi(a.periode.litres, 'gasoilPeriode', `Gasoil de la période — ${label}`, ids)}>{fmtL(a.periode.litres)}</Clic></td>
      <td><Clic onClick={voirSi(a.periode.heuresMat, 'heuresPeriode', `Heures matériel de la période — ${label}`, ids)}>{fmtH(a.periode.heuresMat)}</Clic></td>
      <td><Clic onClick={voirSi(a.periode.repNb, 'reparationsPeriode', `Réparations de la période — ${label}`, ids)}>{a.periode.repMt ? fmtMt(a.periode.repMt) : '—'}</Clic></td>
      <td style={{ color: a.surv.vidDepasse ? '#b71c1c' : undefined }}>
        <Clic onClick={voirSi(a.surv.vidDepasse + a.surv.vidCritique + a.surv.vidAlerte, 'vidanges', `Vidanges — ${label}`, ids)}>
          {fmtNb(a.surv.vidDepasse)} / {fmtNb(a.surv.vidCritique)} / {fmtNb(a.surv.vidAlerte)}
        </Clic>
      </td>
      <td style={{ color: a.surv.citernesBas ? '#b71c1c' : undefined }}>
        <Clic onClick={voirSi(a.surv.citernesBas, 'citernesBas', `Citernes en stock bas — ${label}`, ids)}>{fmtNb(a.surv.citernesBas)}</Clic>
      </td>
    </tr>
  );

  return (
    <div>
      {/* Score */}
      <SectionTitle>⭐ {data.score.axes ? 'Santé du dashboard' : 'Score du dashboard'}</SectionTitle>
      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        <div style={{ border: `2px solid ${note.color}`, borderRadius: 10, padding: '4px 12px', color: note.color, fontWeight: 800 }}>
          {note.emoji} {data.score.global}/100 · {note.label}
          {diff !== 0 && <small style={{ fontWeight: 600 }}> ({diff > 0 ? '+' : ''}{diff} vs veille)</small>}
        </div>
        <InfoTip text={data.score.axes
          ? 'Même score que le bandeau santé du dashboard : moyenne pondérée de 5 taux notés sur 100 (état au jour de référence).'
          : 'Moyenne pondérée de 7 indicateurs notés sur 100 (ancien score du dashboard, rapport enregistré avant le score v2).'} />
        {data.score.axes && data.score.axes.map(a => (
          <span key={a.code} title={a.info} style={{ background: `${lvColor(a.score)}14`, color: lvColor(a.score), borderRadius: 20, padding: '2px 10px', fontSize: '0.78rem' }}>
            ● {a.label} : <strong>{a.score} %</strong> <small>({fmtNb(a.valeur)}/{fmtNb(a.total)}) ×{a.w}</small>
          </span>
        ))}
        {!data.score.axes && data.score.kpis.map(k => {
          const d = KPI_DETAIL[k.code];
          const n = nbKpi[k.code];
          return (
            <span key={k.code} style={{ background: `${lvColor(k.score)}14`, color: lvColor(k.score), borderRadius: 20, padding: '2px 10px', fontSize: '0.78rem' }}>
              ● {k.label} : <Clic onClick={d && n ? voir(d[0], d[1]) : null}><strong>{k.unit === '%' ? `${k.score} %` : fmtNb(n)}</strong></Clic>
              {k.unit !== '%' && <small> ({k.score}/100)</small>} <small style={{ color: '#aaa' }}>×{k.w}</small>
            </span>
          );
        })}
      </div>

      <SectionTitle>🗓 Hier ({fmtDateFr(data.date)})</SectionTitle>
      <div className="row">
        <Stat label="Ouvriers pointés" value={fmtNb(T.hier.ouvNb)} sub={`${fmtNb(T.hier.ouvJours)} j · ${fmtNb(T.hier.ouvHeures)} h sup`}
          onClick={voirSi(T.hier.ouvNb, 'ouvriersHier', 'Ouvriers pointés hier')}
          info="Lignes de pointage du jour de référence (table pointages)." />
        <Stat label="Coût main-d'œuvre" value={`${fmtMt(T.hier.cout)} DH`}
          onClick={voirSi(T.hier.ouvNb, 'ouvriersHier', 'Coût main-d\'œuvre d\'hier')}
          info="Taux journalier appliqué × (jours + heures / 10), comme la masse salariale du dashboard." />
        <Stat label="Matériels pointés" value={fmtNb(T.hier.matNb)} sub={fmtH(T.hier.matHeures)}
          onClick={voirSi(T.hier.matNb, 'materielsHier', 'Matériels pointés hier')}
          info="Pointages matériel du jour de référence ; heures = heures réelles déclarées." />
        <Stat label="Gasoil" value={fmtL(T.hier.litres)}
          onClick={voirSi(T.hier.litres, 'materielsHier', 'Gasoil d\'hier par matériel')}
          info="Litres saisis dans les pointages matériel du jour." />
        <Stat label="Écarts compteur" value={fmtNb(T.hier.ecarts)} alert={T.hier.ecarts > 0}
          onClick={voirSi(T.hier.ecarts, 'materielsEcart', 'Écarts compteur suspects d\'hier')}
          info="Pointages matériel signalés « écart suspect » entre compteur et heures déclarées." />
        <Stat label="Réparations" value={`${fmtMt(T.hier.repMt)} DH`} sub={`${fmtNb(T.hier.repNb)} ligne(s)`}
          onClick={voirSi(T.hier.repNb, 'reparationsHier', 'Réparations d\'hier')}
          info="Lignes de réparation dont la date de BDC est le jour de référence (qté × prix)." />
      </div>

      <SectionTitle>📅 Période de paie ({fmtDateFr(data.periode.start)} → {fmtDateFr(data.date)})</SectionTitle>
      <div className="row">
        <Stat label="Ouvriers pointés" value={`${pctOuvriers(data) ?? '—'} %`} sub={`${fmtNb(o.actifsNonBloques)} / ${fmtNb(o.nonBloques)} non bloqués`}
          info="Ouvriers non bloqués ayant au moins un pointage depuis le 21 / ouvriers non bloqués (DASH-01)." />
        <Stat label="Masse salariale" value={`${fmtMt(T.periode.masse)} DH`} sub={`${fmtNb(T.periode.jours)} j pointés`}
          info="Ouvriers non bloqués : salaire × (jours + heures / 10), par chantier d'affectation — comme le dashboard." />
        <Stat label="Gasoil consommé" value={fmtL(T.periode.litres)}
          onClick={voirSi(T.periode.litres, 'gasoilPeriode', 'Gasoil de la période par matériel')}
          info="SUM(pointage_materiels.gasoil_l) sur la période (DASH-10)." />
        <Stat label="Heures matériel" value={fmtH(T.periode.heuresMat)}
          onClick={voirSi(T.periode.heuresMat, 'heuresPeriode', 'Heures matériel de la période')}
          info="Heures réelles des pointages matériel sur la période." />
        <Stat label="Réparations" value={`${fmtMt(T.periode.repMt)} DH`} sub={`${fmtNb(T.periode.repNb)} ligne(s)`}
          onClick={voirSi(T.periode.repNb, 'reparationsPeriode', 'Réparations de la période')}
          info="Réparations matériel de la période (qté × prix) — DASH-11." />
      </div>

      {data.comparaison && (() => {
        const { actuel: a, reference: r, start, end } = data.comparaison;
        const carte = (label, va, vr, f, inverse) => {
          const pct = vr > 0 ? Math.round(((va - vr) * 100) / vr) : null;
          const bon = pct == null ? null : (inverse ? pct <= 0 : pct >= 0);
          return (
            <Stat label={label} value={f(va)}
              sub={<span style={{ color: bon == null ? undefined : bon ? '#2e7d32' : '#c62828' }}>vs {f(vr)}{pct != null ? ` (${pct > 0 ? '+' : ''}${pct} %)` : ''}</span>} />
          );
        };
        return (
          <>
            <SectionTitle>↔️ Comparaison avec le {fmtDateFr(start)} → {fmtDateFr(end)} <InfoTip text="Période de paie en cours comparée à la période choisie, avec le même calcul pour les deux (masse au taux appliqué, comme le dashboard)." /></SectionTitle>
            <div className="row">
              {carte('Masse salariale', a.masse, r.masse, v => `${fmtMt(v)} DH`)}
              {carte('Jours pointés', a.jours, r.jours, fmtNb)}
              {carte('Gasoil', a.litres, r.litres, fmtL, true)}
              {carte('Heures matériel', a.heuresMateriel, r.heuresMateriel, fmtH)}
              {carte('Réparations', a.reparations, r.reparations, v => `${fmtMt(v)} DH`, true)}
            </div>
          </>
        );
      })()}

      <SectionTitle>🔔 État actuel</SectionTitle>
      <div className="row">
        <Stat label="Vidanges dépassées" value={fmtNb(T.surv.vidDepasse)} alert={T.surv.vidDepasse > 0}
          sub={`${fmtNb(T.surv.vidCritique)} critiques · ${fmtNb(T.surv.vidAlerte)} alertes`}
          onClick={voirSi(T.surv.vidDepasse + T.surv.vidCritique + T.surv.vidAlerte, 'vidanges', 'Vidanges dépassées, critiques et en alerte')}
          info="Matériels des sociétés suivies (Rutas, Pistas, Cadex, Brostol, Elite Parcs) selon leurs propres seuils de vidange." />
        <Stat label="Citernes stock bas" value={fmtNb(T.surv.citernesBas)} alert={T.surv.citernesBas > 0} sub={`stock total ${fmtL(T.surv.stock)}`}
          onClick={voir('citernes', 'Citernes actives')}
          info="Citernes actives dont le stock réel est ≤ capacité minimum (DASH-04)." />
        <Stat label="Matériels en panne" value={fmtNb(data.etat.enPanne)} alert={data.etat.enPanne > 0}
          sub={`${fmtNb(data.etat.bloques)} bloqués (hors parc) · ${fmtNb(data.etat.totalMateriels)} en parc`}
          info="État « en panne » ou « en maintenance » (DASH-08). Les matériels « bloqué » sont hors parc." />
      </div>

      <SectionTitle>🏗 Par société et par chantier</SectionTitle>
      <div className="table-responsive">
        <table className="table table-bordered table-sm" style={{ fontSize: '0.8rem' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Société / Chantier <InfoTip text="Société = société du chantier. Cliquez sur un chiffre souligné pour voir le détail." /></th>
              <th>Ouvriers hier</th>
              <th>Matériels hier</th>
              <th>Masse période <InfoTip text="Masse salariale des ouvriers non bloqués affectés au chantier, et jours pointés sur le chantier." /></th>
              <th>Gasoil période</th>
              <th>Heures mat.</th>
              <th>Réparations</th>
              <th>Vidanges <InfoTip text="Dépassées / critiques / alertes (matériels affectés au chantier)." /></th>
              <th>Citernes bas</th>
            </tr>
          </thead>
          <tbody>
            {calc.societes.map(s => (
              <React.Fragment key={s.societe}>
                {ligne(`🏢 ${s.societe}`, s.agg, s.ids, { bold: true })}
                {s.chantiers.map(c => ligne(c.nom, c.agg, c.ids, { indent: true }))}
              </React.Fragment>
            ))}
            {ligne('TOTAL', T, allIds, { bold: true })}
          </tbody>
        </table>
      </div>

      <DetailModal data={data} detail={detail} onClose={() => setDetail(null)} />
    </div>
  );
};

export default RapportViewTechnique;
